<!-- eslint-disable max-len -->
<template>
  <div class="triage-tickets-card">
    <div
      v-if="loading"
      class="flex items-center justify-center py-8">
      <div class="flex items-center text-gray-500">
        <svg
          class="animate-spin h-5 w-5 mr-3"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24">
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4" />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span class="text-sm font-medium">Loading tickets...</span>
      </div>
    </div>

    <div
      v-else
      class="space-y-4">
      <!-- Create New Ticket Form -->
      <div
        v-if="showCreateForm"
        class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h4 class="text-sm font-semibold text-gray-900 mb-3">
          Create New Ticket
        </h4>
        <div class="space-y-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              v-model="newTicket.title"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Brief description of the issue">
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              v-model="newTicket.description"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Additional details (optional)" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              v-model="newTicket.priority"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
              <option value="0">
                Low
              </option>
              <option value="1">
                Medium
              </option>
              <option value="2">
                High
              </option>
              <option value="3">
                Urgent
              </option>
            </select>
          </div>
          <div class="flex justify-end space-x-2">
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              @click="cancelCreate">
              Cancel
            </button>
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 transition-colors"
              @click="createTicket">
              Create Ticket
            </button>
          </div>
        </div>
      </div>

      <!-- Add Ticket Button -->
      <button
        v-if="!showCreateForm"
        type="button"
        class="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors"
        @click="showCreateForm = true">
        <i class="fas fa-plus mr-2" />
        Add Triage Ticket
      </button>

      <!-- Tickets List -->
      <div
        v-if="tickets.length > 0"
        class="space-y-3">
        <div
          v-for="ticket in tickets"
          :key="ticket.id"
          class="p-4 bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
          :class="getTicketBorderClass(ticket)">
          <!-- Edit Mode -->
          <div v-if="ticket.editing">
            <div class="space-y-3 mb-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  v-model="ticket.editTitle"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  v-model="ticket.editDescription"
                  rows="3"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  v-model="ticket.editPriority"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                  <option value="0">
                    Low
                  </option>
                  <option value="1">
                    Medium
                  </option>
                  <option value="2">
                    High
                  </option>
                  <option value="3">
                    Urgent
                  </option>
                </select>
              </div>
              <div class="flex justify-end space-x-2">
                <button
                  type="button"
                  class="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  @click="cancelEdit(ticket)">
                  Cancel
                </button>
                <button
                  type="button"
                  class="px-3 py-1 text-xs font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 transition-colors"
                  @click="saveEdit(ticket)">
                  Save Changes
                </button>
              </div>
            </div>
          </div>

          <!-- Display Mode -->
          <div v-else>
            <div class="flex items-start justify-between mb-2">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-1">
                  <h5 class="text-sm font-semibold text-gray-900">
                    {{ ticket.title }}
                  </h5>
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                    :class="getStateBadgeClass(ticket.aasmState)">
                    {{ ticket.displayState }}
                  </span>
                  <span
                    v-if="ticket.priority > 0"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                    :class="getPriorityBadgeClass(ticket.priority)">
                    {{ ticket.displayPriority }}
                  </span>
                </div>
                <p
                  v-if="ticket.description"
                  class="text-sm text-gray-600 mt-1">
                  {{ ticket.description }}
                </p>
              </div>
              <div class="flex gap-1">
                <button
                  type="button"
                  class="ml-2 p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
                  title="Edit"
                  @click="startEdit(ticket)">
                  <i class="fas fa-edit text-sm" />
                </button>
                <button
                  type="button"
                  class="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  title="Delete"
                  @click="deleteTicket(ticket)">
                  <i class="fas fa-trash text-sm" />
                </button>
              </div>
            </div>

            <!-- Ticket Meta -->
            <div class="flex items-center gap-4 text-xs text-gray-500 mb-3">
              <span>
                <i class="fas fa-user mr-1" />
                {{ ticket.reporter.name }}
              </span>
              <span>
                <i class="fas fa-clock mr-1" />
                {{ formatDate(ticket.createdAt) }}
              </span>
              <span v-if="ticket.assignedTo">
                <i class="fas fa-user-tag mr-1" />
                Assigned: {{ ticket.assignedTo.name }}
              </span>
            </div>

            <!-- State Transition Buttons -->
            <div class="flex items-center gap-2">
              <button
                v-if="ticket.aasmState === 'open' || ticket.aasmState === 'in_progress'"
                type="button"
                class="px-3 py-1 text-xs font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700 transition-colors"
                @click="transitionState(ticket, 'mark_pending')">
                <i class="fas fa-pause mr-1" />
                Pending
              </button>
              <button
                v-if="ticket.aasmState === 'open' || ticket.aasmState === 'pending' || ticket.aasmState === 'resolved' || ticket.aasmState === 'closed'"
                type="button"
                class="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                @click="transitionState(ticket, 'start')">
                <i class="fas fa-play mr-1" />
                Start
              </button>
              <button
                v-if="ticket.aasmState === 'open' || ticket.aasmState === 'pending' || ticket.aasmState === 'in_progress'"
                type="button"
                class="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors"
                @click="transitionState(ticket, 'resolve')">
                <i class="fas fa-check mr-1" />
                Resolve
              </button>
              <button
                v-if="ticket.aasmState === 'open' || ticket.aasmState === 'pending' || ticket.aasmState === 'in_progress' || ticket.aasmState === 'resolved'"
                type="button"
                class="px-3 py-1 text-xs font-medium text-white bg-gray-600 rounded hover:bg-gray-700 transition-colors"
                @click="transitionState(ticket, 'close')">
                <i class="fas fa-times mr-1" />
                Close
              </button>
              <button
                v-if="ticket.aasmState === 'resolved' || ticket.aasmState === 'closed'"
                type="button"
                class="px-3 py-1 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700 transition-colors"
                @click="transitionState(ticket, 'reopen')">
                <i class="fas fa-redo mr-1" />
                Reopen
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="tickets.length === 0 && !showCreateForm"
        class="text-center py-8">
        <i class="fas fa-ticket-alt text-4xl text-gray-300 mb-3" />
        <p class="text-sm text-gray-500">
          No triage tickets yet
        </p>
        <p class="text-xs text-gray-400 mt-1">
          Click "Add Triage Ticket" to create one
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import mdtoast from '@dmuy/toast'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

const tickets = ref([])
const loading = ref(false)
const showCreateForm = ref(false)
const newTicket = ref({
  title: '',
  description: '',
  priority: '0'
})

const loadTickets = async() => {
  loading.value = true
  try {
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/triage_tickets`)
    tickets.value = response.data.tickets || []
  } catch (error) {
    mdtoast.error('Failed to load tickets', { duration: 3000 })
  } finally {
    loading.value = false
  }
}

const createTicket = async() => {
  if (!newTicket.value.title) {
    mdtoast.error('Please enter a ticket title', { duration: 3000 })
    return
  }

  try {
    const response = await axios.post(
      `/ajax/organizations/${props.organizationId}/triage_tickets`,
      { triage_ticket: newTicket.value }
    )

    if (response.data.success) {
      tickets.value.unshift(response.data.ticket)
      newTicket.value = { title: '', description: '', priority: '0' }
      showCreateForm.value = false
      mdtoast.success('Ticket created successfully', { duration: 3000 })
    }
  } catch (error) {
    mdtoast.error('Failed to create ticket', { duration: 3000 })
  }
}

const cancelCreate = () => {
  newTicket.value = { title: '', description: '', priority: '0' }
  showCreateForm.value = false
}

const deleteTicket = async(ticket) => {
  // eslint-disable-next-line no-restricted-globals, no-alert
  if (!confirm('Are you sure you want to delete this ticket?')) {
    return
  }

  try {
    await axios.delete(`/ajax/triage_tickets/${ticket.id}`)
    tickets.value = tickets.value.filter((t) => t.id !== ticket.id)
    mdtoast.success('Ticket deleted', { duration: 3000 })
  } catch (error) {
    mdtoast.error('Failed to delete ticket', { duration: 3000 })
  }
}

const startEdit = (ticket) => {
  ticket.editing = true
  ticket.editTitle = ticket.title
  ticket.editDescription = ticket.description
  ticket.editPriority = String(ticket.priority)
}

const cancelEdit = (ticket) => {
  ticket.editing = false
  delete ticket.editTitle
  delete ticket.editDescription
  delete ticket.editPriority
}

const saveEdit = async(ticket) => {
  if (!ticket.editTitle) {
    mdtoast.error('Please enter a ticket title', { duration: 3000 })
    return
  }

  try {
    const response = await axios.put(
      `/ajax/triage_tickets/${ticket.id}`,
      {
        triage_ticket: {
          title: ticket.editTitle,
          description: ticket.editDescription,
          priority: ticket.editPriority
        }
      }
    )

    if (response.data.success) {
      const index = tickets.value.findIndex((t) => t.id === ticket.id)
      if (index !== -1) {
        tickets.value[index] = response.data.ticket
      }
      mdtoast.success('Ticket updated successfully', { duration: 3000 })
    }
  } catch (error) {
    mdtoast.error('Failed to update ticket', { duration: 3000 })
  }
}

const transitionState = async(ticket, event) => {
  try {
    const response = await axios.post(`/ajax/triage_tickets/${ticket.id}/${event}`)

    if (response.data.success) {
      const index = tickets.value.findIndex((t) => t.id === ticket.id)
      if (index !== -1) {
        tickets.value[index] = response.data.ticket
      }
      mdtoast.success(response.data.message, { duration: 3000 })
    }
  } catch (error) {
    mdtoast.error(`Failed to ${event} ticket`, { duration: 3000 })
  }
}

const getStateBadgeClass = (state) => {
  const classes = {
    open: 'bg-gray-100 text-gray-800',
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-red-100 text-red-800'
  }
  return classes[state] || 'bg-gray-100 text-gray-800'
}

const getPriorityBadgeClass = (priority) => {
  const classes = {
    0: 'bg-gray-100 text-gray-700',
    1: 'bg-yellow-100 text-yellow-800',
    2: 'bg-orange-100 text-orange-800',
    3: 'bg-red-100 text-red-800'
  }
  return classes[priority] || 'bg-gray-100 text-gray-700'
}

const getTicketBorderClass = (ticket) => {
  if (ticket.priority === 3) return 'border-red-300'
  if (ticket.priority === 2) return 'border-orange-300'
  if (ticket.aasmState === 'pending') return 'border-yellow-200'
  if (ticket.aasmState === 'open' || ticket.aasmState === 'in_progress') return 'border-blue-200'
  return 'border-gray-200'
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return 'Invalid Date'
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

onMounted(() => {
  loadTickets()
})
</script>

<style scoped>
.triage-tickets-card {
  min-height: 100px;
}
</style>
